import { useEffect } from "react";
import { getMetaData } from "../../utils/Constant";

const ConvertJpgToPngBlog = () => {

  // Set title and metadata
  useEffect(() => {
    const metaData = getMetaData("convert-jpg-to-png-meta-data");

    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description; // Use .content instead of setAttribute
  }, []);

  
  return (
    <div className="container">
      <article className="bg-white rounded-4 shadow-lg border-0 mt-5 mb-5 overflow-hidden">
        {/* Header */}
        <header
          className="text-center py-5 px-3 position-relative"
          style={{
            background: "linear-gradient(135deg, #0d6efd 0%, #0dcaf0 100%)",
            color: "#fff",
          }}
        >
          <h1 className="display-5 fw-bold text-white mb-2 mt-4">
            Convert JPG to PNG — 3 Quick and Easy Methods
          </h1>
          <p className="lead mb-0 position-relative fs-4 mb-4">
            Learn three easy ways to convert JPG to PNG while preserving image
            quality and transparency.
          </p>
        </header>

        {/* Main Content */}
        <div className="px-lg-5 px-3 py-5">
          {/* Introduction */}
          <section className="mb-5">
            <p className="lead">
              Need a transparent background for a logo or want to preserve image
              quality? Converting JPG to PNG is the solution. JPG is great for
              photos, while PNG is perfect for graphics, illustrations, and
              images requiring transparency or lossless quality.
            </p>
            <p className="lead">
              In this guide, we'll cover three simple methods to convert JPG
              images to PNG and introduce our free, built-in converter for
              instant results!
            </p>
          </section>

          {/* Why Convert Section */}
          <section className="alert  rounded-4 mb-5">
            <h5 className="alert-heading fs-5 mb-3">
              🤔 Why Convert from JPG to PNG?
            </h5>
            <ul className="mb-3">
              <li className="mb-2">
                <strong>Transparency:</strong> PNG supports transparent
                backgrounds—ideal for logos and web graphics.
              </li>
              <li className="mb-2">
                <strong>Lossless Compression:</strong> PNG retains all image
                data; JPG uses lossy compression, which can degrade quality.
              </li>
              <li>
                <strong>Text and Sharp Lines:</strong> PNG handles sharp edges
                better, preventing blurry artifacts.
              </li>
            </ul>
            <div className="d-flex gap-2 flex-wrap">
              <a
                href="https://samplefiles.dev/sample-images/sample-jpg"
                className="btn btn-primary btn-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download Sample JPG
              </a>
              <a
                href="https://samplefiles.dev/sample-images/sample-png"
                className="btn btn-outline-primary btn-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download Sample PNG
              </a>
            </div>
          </section>

          {/* Method 1 */}
          <section className="mb-5">
            <h2 className="h3 mb-4 text-dark">
              Method 1: Use Our Free JPG to PNG Converter
            </h2>
            <div className="card shadow-sm border-0 bg-light rounded-4 p-3">
              <div className="card-body">
                <h5 className="card-title text-black mb-3">
                  Step-by-Step Guide
                </h5>
                <ol className="mt-3">
                  <li className="mb-2">
                    Visit our{" "}
                    <a
                      href="https://samplefiles.dev/convert-jpg-to-png"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      JPG to PNG Converter
                    </a>{" "}
                    page.
                  </li>
                  <li className="mb-2">
                    Upload your JPG by drag-and-drop or browse your computer.
                  </li>
                  <li className="mb-2">
                    The conversion happens automatically in your browser.
                  </li>
                  <li>Download your PNG file instantly.</li>
                </ol>
                <p className="mt-3">
                  <strong>Why use our tool?</strong> Free, no registration, and
                  works locally for privacy.
                </p>
                <div className="fst-italic text-body-secondary fw-medium border-top pt-2 mt-3">
                  <strong>Best for:</strong> Quick, secure, and hassle-free
                  conversions.
                </div>
              </div>
            </div>
          </section>

          {/* Method 2 */}
          <section className="mb-5">
            <h2 className="h3 mb-4 text-dark">
              Method 2: Using a Free Online Converter
            </h2>
            <div className="card shadow-sm border-0 bg-light rounded-4 p-3">
              <div className="card-body">
                <h5 className="card-title text-black mb-3">
                  Step-by-Step Guide
                </h5>
                <ol className="mt-3">
                  <li className="mb-2">
                    Use websites like CloudConvert or Convertio.
                  </li>
                  <li className="mb-2">Upload your JPG file.</li>
                  <li className="mb-2">Select PNG as the output format.</li>
                  <li>Convert and download the PNG file.</li>
                </ol>
                <div className="fst-italic text-body-secondary fw-medium border-top pt-2 mt-3">
                  <strong>Best for:</strong> One-off conversions or exploring
                  multiple tools.
                </div>
              </div>
            </div>
          </section>

          {/* Method 3 */}
          <section className="mb-5">
            <h2 className="h3 mb-4 text-dark">
              Method 3: Using Built-in Software
            </h2>
            <div className="card shadow-sm border-0 bg-light rounded-4 p-3">
              <div className="card-body">
                <h5 className="card-title text-black">On Windows (Paint)</h5>
                <ol className="mt-3">
                  <li className="mb-2">
                    Right-click JPG → Open with &gt; Paint.
                  </li>
                  <li className="mb-2">File &gt; Save as → PNG (*.png).</li>
                  <li>Name and save the file.</li>
                </ol>

                <h5 className="card-title text-black mt-4">
                  On macOS (Preview)
                </h5>
                <ol className="mt-3">
                  <li className="mb-2">Open JPG in Preview.</li>
                  <li className="mb-2">
                    File &gt; Export → select PNG format.
                  </li>
                  <li>Click Save.</li>
                </ol>
                <div className="fst-italic text-body-secondary fw-medium border-top pt-2 mt-3">
                  <strong>Best for:</strong> Quick conversions without uploading
                  files.
                </div>
              </div>
            </div>
          </section>

          {/* Quality Note */}
          <section className="alert alert-warning rounded-4 p-4 mb-5">
            <h5 className="alert-heading fs-6 mb-2">⚠️ Note on Quality</h5>
            <p className="mb-0">
              Converting JPG to PNG{" "}
              <strong>won’t improve original quality</strong>. PNG prevents
              further loss, but artifacts in JPG remain.
            </p>
            <h5 className="alert-heading fs-6 mt-4 mb-2">
              🔄 Converting PNG to JPG?
            </h5>
            <p className="mb-0">
              Use our{" "}
              <a
                href="https://samplefiles.dev/convert-jpg-to-png"
                target="_blank"
                rel="noopener noreferrer"
                className="fw-bold"
              >
                PNG to JPG Converter
              </a>{" "}
              to reduce file size while keeping good quality.
            </p>
          </section>

          {/* Conclusion */}
          <section className="mb-5">
            <h2 className="h3 mb-4 text-dark">Conclusion</h2>
            <p>
              Converting JPG to PNG is straightforward with the right tools. Use
              our converter for instant results or built-in software for local
              conversions. You're ready for any image conversion task!
            </p>
            <div className="alert alert-success rounded-4 p-3">
              <strong>Pro Tip:</strong> For projects needing transparency or
              lossless editing, save original files as PNG from the start.
            </div>
          </section>

          {/* Tags */}
          <footer className="border-top pt-4">
            <div className="d-flex  flex-wrap gap-2">
              {[
                "convert jpg to png",
                "image format",
                "png vs jpg",
                "how-to guide",
                "transparency",
                "free online converter",
                "jpg to png converter",
              ].map((tag) => (
                <span key={tag} className="badge bg-secondary p-2">
                  {tag}
                </span>
              ))}
            </div>
          </footer>
        </div>
      </article>
    </div>
  );
};

export default ConvertJpgToPngBlog;
